// Creature skins: an alternate look for every creature of one species that a
// trainer owns. Looks only: a skin never changes stats, odds, grade or value.
//
// A skin belongs to the account, not to a creature. It is bought once in the
// Diamond shop, can't be traded or listed, and the owner turns it on or off per
// species ("use skin"); while on, every creature of that species they own shows
// it, to them and to everyone who sees those creatures (showcase, companion,
// arena line-up). A creature that changes hands shows its normal look to the new
// owner, unless they own and use the same skin.
//
// Art (mythic-idle-design/skins-format.md): a skin ships the same five files as
// its creature, named `<species>~<skin>.png` (sheets/, the portrait, walk/, mini/,
// mini-lg/), and creatures/manifest.json lists it on the species as
// `"skins": ["halloween"]`. The client draws the skin only when the manifest
// lists it, so a skin sold before its art lands shows the normal look.
//
// Aura wins: an Aura creature always shows its Aura look, never a skin. Awakened
// forms are separate species ids and are not covered by their base's skin.
import type { TrainerDTO } from './protocol.ts';
import { SPECIES } from './species.ts';

/** Diamond price of a skin (pokeidle sells skins at 30 diamonds). */
export const SKIN_PRICE = 30;

export type SkinSeason = 'halloween' | 'frostfall' | 'lanternnight';

export interface SkinSeasonDef {
  id: SkinSeason;
  name: string;
  /** Sold in the shop by default; an admin can close or reopen the window (kv 'skin-seasons'). */
  available: boolean;
  /** Given by the season pass only: never on sale, whatever an admin sets. */
  passOnly?: boolean;
}

export const SKIN_SEASONS: Record<SkinSeason, SkinSeasonDef> = {
  halloween: { id: 'halloween', name: 'Halloween', available: true },
  /** Season pass only (shared/pass.ts): never sold. */
  frostfall: { id: 'frostfall', name: 'Frostfall', available: false, passOnly: true },
  lanternnight: { id: 'lanternnight', name: 'Lantern Night', available: false, passOnly: true },
};

export interface SkinDef {
  /** Skin id; also its sprite id in the art build (`<species>~<skin>`). */
  id: string;
  /** The species it reskins (a final form). */
  species: string;
  /** The skin word in the art manifest's `skins` list for the species. */
  skin: string;
  name: string;
  price: number;
  season: SkinSeason;
  /** For the art thread: what to draw. */
  look: string;
}

const skin = (species: string, season: SkinSeason, name: string, look: string): SkinDef =>
  ({ id: `${species}~${season}`, species, skin: season, name, price: SKIN_PRICE, season, look });

/** The three Halloween skins: the final forms of the three starter lines (Nykling, Kitling, Leafcub). */
export const SKINS: SkinDef[] = [
  skin('brookstallion', 'halloween', 'Nightmare Brook Stallion', 'Ink-black water horse, mane of dark water with drifting green wisp lights, pale glowing eyes'),
  skin('chimera', 'halloween', "Jack-o'-Chimera", 'Pumpkin-orange lion with a carved lantern glow in its chest, goat head with a witch-hat tuft, vine serpent tail'),
  skin('guisewarden', 'halloween', 'Lantern Guisewarden', 'Raccoon-dog in a straw coat of red autumn leaves, a paper lantern in place of the sake jug, purple disguise smoke'),
  // Season pass rewards (shared/pass.ts): never sold, so their season stays closed.
  skin('fenrisborn', 'frostfall', 'Frostfall Fenrisborn', 'Regal winter wolf: pale rime-white fur with glacier-blue shading, a mantle of icicles along the spine, the broken chain turned to frosted silver, faint snow drifting off the coat'),
  skin('ninefold', 'lanternnight', 'Lantern Ninefold', 'Snow-white fox of the winter lantern festival: a fan of tails each tipped with a small glowing red paper lantern, vermilion and gold festival cords, indigo night markings, soft warm lantern light and drifting sparks'),
];

export const SKIN: Record<string, SkinDef> = Object.fromEntries(SKINS.map((s) => [s.id, s]));

/** Season open overrides set by an admin: season id → open. Missing means the season's `available` default. */
export type SkinWindows = Partial<Record<SkinSeason, boolean>>;

export function seasonOpen(season: SkinSeason, windows: SkinWindows = {}): boolean {
  if (SKIN_SEASONS[season]?.passOnly) return false;
  return windows[season] ?? SKIN_SEASONS[season]?.available ?? false;
}

/** Seasons whose skins the shop sells now. */
export function openSeasons(windows: SkinWindows = {}): SkinSeason[] {
  return (Object.keys(SKIN_SEASONS) as SkinSeason[]).filter((s) => seasonOpen(s, windows));
}

/** Skins on sale now. */
export function skinsForSale(windows: SkinWindows = {}): SkinDef[] {
  return SKINS.filter((s) => seasonOpen(s.season, windows));
}

type SkinOwner = Pick<TrainerDTO, 'skins' | 'skinsOn'>;

export function ownsSkin(t: SkinOwner | undefined, id: string): boolean {
  return !!SKIN[id] && !!t?.skins?.includes(id);
}

/** The skin id an owner shows on their creatures of this species, or undefined for the normal look. */
export function skinOf(t: SkinOwner | undefined, species: string): string | undefined {
  if (!t?.skinsOn?.length) return undefined;
  return t.skinsOn.find((id) => SKIN[id]?.species === species && ownsSkin(t, id));
}

type Auraish = { aura?: boolean; rolls?: { aura: boolean } };
const isAura = (x: Auraish | undefined) => !!(x?.aura || x?.rolls?.aura);

/**
 * A creature view (or companion ref) with the owner's skin added, for other players to draw. `src` is the owner's
 * creature it was made from: a listed one shows its base look, and an Aura never takes a skin.
 */
export function withSkin<T extends { species: string } & Auraish>(x: T, owner: SkinOwner | undefined, src?: { listed?: boolean } & Auraish): T & { skin?: string } {
  if (isAura(x) || isAura(src) || src?.listed) return x;
  const s = skinOf(owner, x.species);
  return s ? { ...x, skin: s } : x;
}

/** Turn a skin on (and every other skin of its species off) or off. Returns the new skinsOn list. */
export function setSkinUse(t: SkinOwner, id: string, on: boolean): string[] {
  const sp = SKIN[id]?.species;
  const rest = (t.skinsOn ?? []).filter((x) => x !== id && (!on || SKIN[x]?.species !== sp));
  return on ? [...rest, id] : rest;
}

/**
 * The sprite id to draw for a creature: `<species>~<skin>` when the skin is real, of that species, the creature is
 * not an Aura, and the art manifest lists the skin (`hasArt(species, skin)`); else the species itself.
 */
export function skinArt(species: string, skinId: string | undefined, aura: boolean, hasArt: (species: string, skin: string) => boolean): string {
  const s = skinId && Object.hasOwn(SKIN, skinId) ? SKIN[skinId] : undefined;
  return s && !aura && s.species === species && hasArt(species, s.skin) ? s.id : species;
}

/** Every skin reskins a real final form. */
export function skinProblems(): string[] {
  const out: string[] = [];
  for (const s of SKINS) {
    const sp = SPECIES[s.species];
    if (!sp) out.push(`${s.id}: unknown species`);
    else if (sp.evolvesTo) out.push(`${s.id}: ${s.species} is not a final form`);
    if (s.id !== `${s.species}~${s.skin}` || !/^[a-z]+$/.test(s.skin)) out.push(`${s.id}: id must be <species>~<skin>, skin lower-case letters`);
  }
  return out;
}
